<template>
  <div class="pre-cut" :class="{ 'pre-cut--mau': isModal, 'pre-cut--multi-size': showMultiSize }">
    <div v-if="showPreCutUpsell" class="pre-cut__upsell">
      <span class="pre-cut__upsell-title">Save {{ preCutSavedTimeText }}</span>
      <br />
      By letting us precut your
      <span class="pre-cut__upsell-quantity">{{ totalQuantityText }}</span>
      <!-- eslint-disable prettier/prettier -->
      <span v-if="productCartQuantity" class="pre-cut__upsell-cart-quantity"
        >, including <span class="pre-cut__upsell-quantity">{{ productCartQuantity }}</span> from
        your cart</span
      >.
      <!-- eslint-enable prettier/prettier -->
    </div>
    <div v-else class="pre-cut__title">Want to save time?</div>
    <div class="pre-cut__checkbox-wrapper">
      <label
        class="pre-cut__label"
        :class="{
          'pre-cut__label--standard-artwork': isStandardArtwork
        }"
      >
        <span class="sr-only">Pre-cut</span>
        <input
          type="checkbox"
          class="pre-cut__input js-pre-cut-checkbox"
          data-testid="transfers-pre-cut-checkbox"
          :checked="preCut"
          :disabled="!enablePreCut"
          @change="togglePreCut"
        />
        <span class="pre-cut__icon-wrapper" data-testid="transfers-pre-cut-option" />
        <UseSvg v-if="preCut" icon="check" class="pre-cut__icon pre-cut__icon--full" />
      </label>
      <div
        :class="{ 'pre-cut__text': true, 'pre-cut__text--bold': preCut }"
        data-testid="transfers-pre-cut-price"
      >
        Pre-cut transfers ({{ n(preCutPrice, "currency") }} each)
      </div>
      <PreCutPopup class="pre-cut__info" />
    </div>
    <div class="pre-cut__upsell-note">This applies to all {{ dtfCategory }} orders</div>
  </div>
</template>

<script>
import { mapActions, mapState } from "pinia";
import UseSvg from "common/vue/components/UseSvg.vue";
import PreCutPopup from "common/vue/components/PreCutPopup.vue";
import { submitDtfEvent } from "common/utils/mixpanel/submit-dtf-events";
import { generateTimeRemainingText } from "common/utils/ui/generate-time-remaining";
import { trackRudderstackImageEdited } from "common/utils/rudderstack/tracker";
import { useI18n } from "vue-i18n";
import { n } from "../vue-cart/helpers/currency";
import { useDtfStore } from "./store";

export default {
  name: "PreCut",
  components: { UseSvg, PreCutPopup },
  props: {
    isModal: {
      type: Boolean,
      default: false
    }
  },
  setup() {
    return { ...useI18n(), n };
  },
  computed: {
    ...mapState(useDtfStore, [
      "preCutPrice",
      "preCut",
      "preCutUpsellThreshold",
      "enablePreCut",
      "experiments",
      "totalQuantity",
      "preCutDuration",
      "productCartQuantity",
      "dtfCategory",
      "showMultiSize",
      "isStandardArtwork",
      "artworks",
      "isSpangle",
      "sticker",
      "businessLine",
      "isProColor"
    ]),
    showPreCutUpsell() {
      const showUpsell = Boolean(this.totalQuantity);
      if (this.preCutUpsellThreshold) {
        const savedMinutes = this.totalSeconds / 60;
        return savedMinutes >= this.preCutUpsellThreshold && showUpsell;
      }
      return showUpsell;
    },
    cartPlusQuantity() {
      return this.productCartQuantity + this.totalQuantity;
    },
    totalQuantityText() {
      return this.t("item", this.cartPlusQuantity);
    },
    preCutSavedTimeText() {
      // convert to milliseconds first
      return generateTimeRemainingText(this.totalSeconds * 1000 + Date.now());
    },
    totalSeconds() {
      // round up to nearest minute
      return Math.ceil((this.preCutDuration * this.cartPlusQuantity) / 60) * 60;
    }
  },
  methods: {
    ...mapActions(useDtfStore, ["updatePreCut"]),
    togglePreCut() {
      this.submitPreCutEvent();
      this.updatePreCut(!this.preCut);
    },
    submitPreCutEvent() {
      const newState = this.preCut ? "disable" : "enable";
      submitDtfEvent("pre_cut_toggle", {
        pre_cut: newState,
        is_upsell: true,
        page: "pdp"
      });

      const detectProductLine = obj => {
        if (obj.sticker) return "sticker";
        if (obj.isProColor) return "pro color";
        if (obj.isSpangle) return "spangle";

        return "dtf";
      };

      trackRudderstackImageEdited({
        product_line: detectProductLine(this),
        artwork: this.artworks[0],
        edit_type: newState === "enable" ? "precut transfer selected" : "precut transfer removed",
        artworks_count: this.artworks.length,
        location: "pdp",
        pre_cut_transfers: this.preCut,
        business_line: this.businessLine
      });
    }
  }
};
</script>

<style scoped lang="scss">
@import "frontend/init/jiffy/settings.scss";
@import "frontend/init/jiffy/mixin.scss";

.pre-cut {
  $this: &;

  border: 1px solid $dark-pink;
  border-radius: 8px;
  background: linear-gradient(92.42deg, $light-pink-3 0%, $light-pink-4 100%);
  padding: 16px 20px;
  margin-bottom: 16px;

  &--multi-size {
    border: none;
    background: $app-gray-modern-50;
  }

  &--mau {
    margin-top: 20px;
  }

  &__title {
    font-size: 16px;
    font-weight: 700;
  }

  &__upsell {
    font-size: 16px;
    line-height: 20px;
  }

  &__upsell-title {
    font-size: 18px;
    font-weight: 700;
  }

  &__upsell-quantity {
    font-weight: 700;
  }

  &__upsell-note {
    margin-top: 14px;
    color: $warm-grey;
  }

  &__checkbox-wrapper {
    display: flex;
    align-items: center;
    margin-top: 10px;
  }

  &__text {
    margin-left: 8px;

    &--bold {
      font-weight: 700;
    }
  }

  &__label {
    display: block;
    width: 20px;
    height: 20px;
    position: relative;
    cursor: pointer;

    &--standard-artwork {
      #{$this}__icon-wrapper {
        border-color: $primary-light;
      }

      #{$this}__input:checked + #{$this}__icon-wrapper {
        background-color: $primary-light;
        border-color: $primary-light;
      }
    }
  }

  &__input {
    @include invisible;
    width: 100%;
    height: 100%;
  }

  &__input:checked + &__icon-wrapper {
    background-color: $bright-pink;
    border-color: $bright-pink;
  }

  &__input:checked ~ &__icon {
    display: block;
  }

  &__icon-wrapper {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    border-radius: 4px;
    background-color: transparent;
    border: 2px solid $bright-pink;
  }

  &__info {
    display: flex;
    height: 100%;
    margin-left: 8px;
  }

  &__icon {
    display: none;
    position: absolute;
    left: 0;
    top: 0;
    color: $white;

    &--full {
      width: 100%;
      height: 100%;
    }
  }
}
</style>
